/**
 * Bar-chart domain: where the baseline sits, and whether that must be disclosed.
 *
 * This exists because the uptime chart was unreadable. Availability sits at
 * 97–98%, and the chart drew it against a fixed 0–100 domain, so every bar came
 * out within one pixel of every other. The customer saw two identical blocks and
 * learned nothing; the variation that mattered was the 3% the chart threw away.
 *
 * The fix is a padded domain, which introduces the opposite hazard: a bar chart
 * whose baseline is not zero exaggerates differences, and one that does not SAY
 * its baseline is not zero is simply deceptive. So this returns `baselineIsZero`
 * and the caller is expected to render the baseline value when it is false.
 * Clipping the axis is a legitimate choice; hiding that you clipped it is not.
 *
 * Pure and separate from the component because the arithmetic is where this goes
 * wrong, and arithmetic is testable while a div's height is not.
 */
export interface BarDomain {
  lo: number;
  hi: number;
  /** False when the axis is clipped, which the chart must disclose. */
  baselineIsZero: boolean;
}

/** Fraction of the value range added as headroom above and below. */
const PADDING = 0.25;

/**
 * @param values the series, in any order
 * @param cap an upper bound the domain may never exceed, e.g. 100 for a percentage
 * @param zeroFloor when the values sit near zero, keep the baseline at zero
 *   rather than inventing a clipped axis for a series that reads fine from the
 *   origin. A duration chart starting at 6h would overstate a 6h-to-8h change.
 */
export function computeBarDomain(
  values: number[],
  { cap, zeroFloor = 0.35 }: { cap?: number; zeroFloor?: number } = {},
): BarDomain {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length === 0) return { lo: 0, hi: cap ?? 1, baselineIsZero: true };

  const min = Math.min(...finite);
  const max = Math.max(...finite);

  // A series that already spans most of its own range reads correctly from
  // zero; clipping it would only exaggerate. `min / max` is that test: when the
  // smallest bar is a small fraction of the largest, zero is already a fair
  // baseline and the chart needs no disclosure.
  if (max <= 0 || min / max <= zeroFloor) {
    return { lo: 0, hi: cap !== undefined ? Math.min(cap, niceCeiling(max)) : niceCeiling(max), baselineIsZero: true };
  }

  const span = max - min;
  // All values identical: give the bar somewhere to sit rather than dividing by
  // zero, and keep the baseline honest by leaving it at zero.
  if (span === 0) {
    return { lo: 0, hi: cap !== undefined ? Math.min(cap, niceCeiling(max)) : niceCeiling(max), baselineIsZero: true };
  }

  const pad = span * PADDING;
  const lo = Math.max(0, Math.floor(min - pad));
  const hi = cap !== undefined ? Math.min(cap, Math.ceil(max + pad)) : Math.ceil(max + pad);

  // The padding can collapse against the cap; never return an empty domain.
  if (hi <= lo) return { lo: 0, hi: cap ?? niceCeiling(max), baselineIsZero: true };

  return { lo, hi, baselineIsZero: lo === 0 };
}

/** Round up to something a reader would pick: 1, 2, 5 × a power of ten. */
function niceCeiling(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 5, 10]) {
    const candidate = step * magnitude;
    if (candidate >= value) return candidate;
  }
  return 10 * magnitude;
}

/** Where a value sits in the domain, as a 0–1 fraction of the plot height. */
export function barFraction(value: number, domain: BarDomain): number {
  const span = domain.hi - domain.lo;
  if (!Number.isFinite(value) || span <= 0) return 0;
  return Math.max(0, Math.min(1, (value - domain.lo) / span));
}

/**
 * How many decimals a label needs before it stops inventing differences.
 *
 * The uptime chart labelled 97.49% and 97.53% as "97%" and "98%". The bars were
 * honest — they drew as near-identical, which is what a 0.04 point change looks
 * like — but the numbers beside them announced a one-point improvement that did
 * not happen. The platform rule is that every number shown traces to a row, and
 * a number rounded until it says something the row does not breaches it as
 * surely as an invented one.
 *
 * The criterion is NOT that two labels must differ — that was the first attempt
 * and it is exactly backwards. 97.49 and 97.53 already render as different
 * integers; the problem is that rounding to a whole number moves each value by
 * up to 0.5 while the real spread is 0.04, so the rounding error swamps the
 * signal and the labels are mostly noise.
 *
 * So: the smallest precision whose step is no larger than the spread the chart
 * is showing. Capped, because past two decimals a reader stops reading.
 */
export function labelPrecision(values: number[], maxDecimals = 2): number {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length < 2) return 0;
  const spread = Math.max(...finite) - Math.min(...finite);
  // Every value identical: there is no difference to misstate.
  if (spread <= 0) return 0;
  for (let decimals = 0; decimals < maxDecimals; decimals += 1) {
    if (10 ** -decimals <= spread) return decimals;
  }
  return maxDecimals;
}
